import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus,
  Truck,
  Wrench,
  Edit,
  Trash2,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

const EQUIPMENT_TYPES = [
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'heavy_machinery', label: 'Heavy Machinery' },
  { value: 'tool', label: 'Tool' },
  { value: 'safety_equipment', label: 'Safety Equipment' }
];

export default function AdminEquipment() {
  const { authAxios } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    equipment_id: '',
    equipment_type: 'vehicle',
    description: '',
    location: '',
    serial_number: '',
    purchase_date: ''
  });

  const fetchEquipment = useCallback(async () => {
    try {
      const res = await authAxios.get('/equipment');
      setEquipment(res.data);
    } catch (error) {
      toast.error('Failed to load equipment');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const resetForm = () => {
    setFormData({
      name: '',
      equipment_id: '',
      equipment_type: 'vehicle',
      description: '',
      location: '',
      serial_number: '',
      purchase_date: ''
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingId) {
        await authAxios.put(`/equipment/${editingId}`, formData);
        toast.success('Equipment updated');
      } else {
        await authAxios.post('/equipment', formData);
        toast.success('Equipment added');
      }
      setDialogOpen(false);
      resetForm();
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save equipment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (equip) => {
    setFormData({
      name: equip.name,
      equipment_id: equip.equipment_id,
      equipment_type: equip.equipment_type,
      description: equip.description || '',
      location: equip.location || '',
      serial_number: equip.serial_number || '',
      purchase_date: equip.purchase_date || ''
    });
    setEditingId(equip.id);
    setDialogOpen(true);
  };

  const handleDelete = async (equip) => {
    if (!window.confirm(`Delete ${equip.name}?`)) return;

    try {
      await authAxios.delete(`/equipment/${equip.id}`);
      toast.success('Equipment deleted');
      fetchEquipment();
    } catch (error) {
      toast.error('Failed to delete equipment');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      out_of_service: 'bg-rose-100 text-rose-700 border-rose-200',
      retired: 'bg-stone-100 text-stone-600 border-stone-200'
    };
    const icons = {
      active: <CheckCircle className="w-3 h-3 mr-1" />,
      out_of_service: <XCircle className="w-3 h-3 mr-1" />,
      retired: <AlertTriangle className="w-3 h-3 mr-1" />
    };
    return (
      <Badge variant="outline" className={`${styles[status]} flex items-center`}>
        {icons[status]}
        {status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </Badge>
    );
  };

  const filteredEquipment = equipment.filter(e => {
    const matchesSearch = e.name.toLowerCase().includes(search.toLowerCase()) ||
                         e.equipment_id.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === 'all' || e.equipment_type === filterType;
    const matchesStatus = filterStatus === 'all' || e.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-equipment">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Equipment Inventory</h1>
          <p className="text-stone-500 mt-1">Manage all equipment, vehicles, and tools</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="add-equipment-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Equipment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-emerald-600" />
                {editingId ? 'Edit Equipment' : 'Add New Equipment'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Ford F-150"
                    data-testid="equipment-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="equipment_id">Equipment ID *</Label>
                  <Input
                    id="equipment_id"
                    value={formData.equipment_id}
                    onChange={(e) => setFormData({ ...formData, equipment_id: e.target.value })}
                    required
                    placeholder="e.g., VH-001"
                    data-testid="equipment-id-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select value={formData.equipment_type} onValueChange={(v) => setFormData({ ...formData, equipment_type: v })}>
                  <SelectTrigger data-testid="equipment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional details..."
                  data-testid="equipment-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Warehouse A"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serial">Serial Number</Label>
                  <Input
                    id="serial"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="purchase_date">Purchase Date</Label>
                <Input
                  id="purchase_date"
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-equipment-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingId ? 'Update' : 'Add'} Equipment
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-lg mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search equipment..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="equipment-search"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {EQUIPMENT_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="out_of_service">Out of Service</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Equipment Table */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Truck className="w-5 h-5 text-stone-400" />
            Equipment ({filteredEquipment.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : filteredEquipment.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No equipment found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-100">
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Name</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">ID</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Type</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Location</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Next Service</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEquipment.map((equip) => (
                    <TableRow key={equip.id} className="border-stone-100 hover:bg-stone-50">
                      <TableCell className="font-medium">{equip.name}</TableCell>
                      <TableCell className="text-stone-600 font-mono text-sm">{equip.equipment_id}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-stone-100 text-stone-700 border-stone-200">
                          {equip.equipment_type?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-stone-600">{equip.location || '-'}</TableCell>
                      <TableCell>{getStatusBadge(equip.status)}</TableCell>
                      <TableCell className="text-stone-600">
                        {equip.next_service_due ? fmtTz(equip.next_service_due, 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(equip)} data-testid={`edit-${equip.id}`}>
                            <Edit className="w-4 h-4 text-stone-600" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(equip)} data-testid={`delete-${equip.id}`}>
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
